import { useState } from "react";
import { KeyRound, Loader2, ShieldCheck, LogOut } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";

export default function ForcePassword() {
  const { user, logout, markPasswordChanged } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (form.new_password !== form.confirm) return toast.error("New passwords do not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password set — welcome!");
      markPasswordChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not set password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="w-full max-w-md p-8 rounded-lg border-slate-200 shadow-sm" data-testid="force-password-card">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-md bg-slate-900 flex items-center justify-center">
            <KeyRound className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-heading text-lg font-bold text-slate-900">Set your password</div>
            <div className="text-xs text-slate-400">Signed in as {user?.email}</div>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          For security, please replace the temporary password with your own private one before continuing.
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="force-password-form">
          <div>
            <Label>Current (temporary) password</Label>
            <Input type="password" data-testid="force-current-password" value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="mt-1 rounded-md" required />
          </div>
          <div>
            <Label>New password</Label>
            <Input type="password" data-testid="force-new-password" value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="mt-1 rounded-md" placeholder="At least 6 characters" required />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type="password" data-testid="force-confirm-password" value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-1 rounded-md" required />
          </div>
          <Button type="submit" data-testid="force-save-password-btn" disabled={saving} className="w-full rounded-md bg-slate-900 hover:bg-slate-800">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            Set Password &amp; Continue
          </Button>
        </form>
        <button onClick={logout} data-testid="force-logout-btn" className="mt-4 w-full text-xs text-slate-400 hover:text-slate-700 flex items-center justify-center gap-1">
          <LogOut className="h-3 w-3" /> Sign out
        </button>
      </Card>
    </div>
  );
}
